import { NumberDetails, Res } from 'common/types/responses'
import { PlanSlug } from 'common/types/enum'
import { Req } from 'common/types/requests'
import { service } from 'common/service'
import { getStore } from 'common/store'
import trackSale from 'common/utils/trackSale'
import { prices } from 'common/utils/planPrice'
import { getApi } from 'common/api'

export const numberService = service
  .enhanceEndpoints({ addTagTypes: ['Number'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      createNumber: builder.mutation<
        Res['number'],
        Req['createNumber'] & { existingPhoneIds?: number[] }
      >({
        invalidatesTags: [{ id: 'LIST', type: 'Number' }],
        query: (query) => ({
          body: {
            forwarding_email_address: query.forwarding_email_address,
            nickname: query.nickname,
            phone_number_type: query.phone_number_type,
            utm_source: getApi().utm_source,
          },
          method: 'POST',
          url: `customers/companies/${query.company_id}/whatsapp-phones/`,
        }),
        async transformResponse(baseQueryReturnValue, meta, arg) {
          try {
            trackSale(
              PlanSlug.WHATSAPP,
              'month',
              (prices.monthly as any)[PlanSlug.WHATSAPP],
              getApi().platform === 'web' ? 'Customer Portal' : 'Mobile App',
            )
          } catch (e) {}
          if (arg.existingPhoneIds?.length) {
            return baseQueryReturnValue as Res['number']
          }
          // Poll for the new number using getNumbers
          const maxAttempts = 30
          const delayMs = 2000
          let newNumber: NumberDetails | null = null

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, delayMs))

            const numbersResult = await getNumbers(
              getStore(),
              {
                company_id: arg.company_id,
              },
              { forceRefetch: true },
            )

            const numbers = numbersResult?.data || []
            newNumber = numbers.find(
              (num: NumberDetails) =>
                !arg.existingPhoneIds!.includes(num.phone_id),
            )

            if (newNumber) break
          }

          if (!newNumber) {
            throw new Error('Failed to provision phone number')
          }

          // Update the new number with nickname and forwarding email
          await updateNumber(getStore(), {
            company_id: arg.company_id,
            forwarding_email_address: arg.forwarding_email_address,
            id: newNumber.phone_id,
            nickname: arg.nickname,
          })

          return baseQueryReturnValue as Res['number']
        },
      }),
      deleteNumber: builder.mutation<Res['number'], Req['deleteNumber']>({
        invalidatesTags: [{ id: 'LIST', type: 'Number' }],
        query: (query: Req['deleteNumber']) => ({
          method: 'DELETE',
          url: `customers/companies/${query.company_id}/whatsapp-phones/${query.id}/`,
        }),
      }),
      getNumbers: builder.query<Res['numbers'], Req['getNumbers']>({
        providesTags: [{ id: 'LIST', type: 'Number' }],
        query: (query: Req['getNumbers']) => ({
          method: 'GET',
          url: `customers/companies/${query.company_id}/whatsapp-phones/`,
        }),
        transformResponse: (response: Res['numbers']) => {
          // Sort numbers: non-cancelled first, then by nickname
          if (!Array.isArray(response)) return response
          return response.slice().sort((a, b) => {
            // First sort by cancelled status (non-cancelled first)
            const aCancelled = !!a.cancelled_on
            const bCancelled = !!b.cancelled_on
            if (aCancelled !== bCancelled) {
              return aCancelled ? 1 : -1
            }
            // Then sort by nickname
            const aName = (a.nickname || '').toLowerCase()
            const bName = (b.nickname || '').toLowerCase()
            return aName.localeCompare(bName)
          })
        },
      }),
      updateNumber: builder.mutation<Res['number'], Req['updateNumber']>({
        invalidatesTags: [{ id: 'LIST', type: 'Number' }],
        query: (query: Req['updateNumber']) => ({
          body: {
            forwarding_email_address: query.forwarding_email_address,
            nickname: query.nickname,
          },
          method: 'PUT',
          url: `customers/companies/${query.company_id}/whatsapp-phones/${query.id}/`,
        }),
      }),
      // END OF ENDPOINTS
    }),
  })

export async function createNumber(
  store: any,
  data: Req['createNumber'],
  options?: Parameters<typeof numberService.endpoints.createNumber.initiate>[1],
) {
  return store.dispatch(
    numberService.endpoints.createNumber.initiate(data, options),
  )
}
export async function getNumbers(
  store: any,
  data: Req['getNumbers'],
  options?: Parameters<typeof numberService.endpoints.getNumbers.initiate>[1],
) {
  return store.dispatch(
    numberService.endpoints.getNumbers.initiate(data, options),
  )
}
export async function updateNumber(
  store: any,
  data: Req['updateNumber'],
  options?: Parameters<typeof numberService.endpoints.updateNumber.initiate>[1],
) {
  return store.dispatch(
    numberService.endpoints.updateNumber.initiate(data, options),
  )
}
export async function deleteNumber(
  store: any,
  data: Req['deleteNumber'],
  options?: Parameters<typeof numberService.endpoints.deleteNumber.initiate>[1],
) {
  return store.dispatch(
    numberService.endpoints.deleteNumber.initiate(data, options),
  )
}
// END OF FUNCTION_EXPORTS

export const {
  useCreateNumberMutation,
  useDeleteNumberMutation,
  useGetNumbersQuery,
  useUpdateNumberMutation,
  // END OF EXPORTS
} = numberService

/* Usage examples:
const { data, isLoading } = useGetNumberQuery({ id: 2 }, {}) //get hook
const [createNumber, { isLoading, data, isSuccess }] = useCreateNumberMutation() //create hook
numberService.endpoints.getNumber.select({id: 2})(store.getState()) //access data from any function
*/
